import axios from 'axios';
import Swal from 'sweetalert2';
import Cookies from 'js-cookie';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ApiKey from 'Components/api/ApiKey';
import BaseApi from 'Components/api/BaseApi';
import { Questionnaire } from './Questionnaire';

import { logOutIfTokenExpired, resultInfo } from 'Components/helpers';

import { useWindowSize } from 'Hooks/use-window-size';
import { Flex, Container, Spinner } from '@radix-ui/themes';
import { AddToFavorites } from './add-to-favorites';
import { DownloadSimpleCvPdf } from './downolad-pdf';
import { observer } from 'mobx-react-lite';

const QuestionnaireView = () => {
   const { id } = useParams();
   // const location = useLocation();
   const navigate = useNavigate();
   const { width } = useWindowSize();
   const { t } = useTranslation('global');
   const userType = Cookies.get('user_type');
   const tokenKey = Cookies.get('tokenClient');

   const [loading, setLoading] = useState(true);
   const [userData, setUserData] = useState(null);
   const [mailSent, setMailSent] = useState(false);
   const [selectedFileName, setSelectedFileName] = useState([]);
   const [errors, setErrors] = useState({ subject: '', message: '' });
   const [favouriteListProfileData, setFavouriteListProfileData] = useState(null);
   const [mailReply, setMailReply] = useState({ subject: '', message: '', emailFiles: [] });

   const getData = async () => {
      try {
         setLoading(true);

         const response = await axios.post(BaseApi + `/jobApply/simplePage/${id}`, null, {
            headers: {
               key: ApiKey,
               token: tokenKey,
               contentType: 'application/json',
            },
         });

         if (response.data.status === 200) {
            setFavouriteListProfileData(response.data.response);
            setUserData(response.data.response);
            setLoading(false);
         } else if (response.data.status === 400) {
            navigate('/');

            Cookies.remove('fname');
            Cookies.remove('user_id');
            Cookies.remove('user_type');
            Cookies.remove('tokenClient');

            resultInfo('warning', response.data.message, t('employerFavouriteList.close'));
         } else {
            Swal.fire({
               icon: 'error',
               text: response.data.message,
               title: t('employerCreateJob.createJobFailedTitle'),
               confirmButtonText: t('employerFavouriteList.close'),
            });
         }
      } catch (error) {
         setLoading(false);

         if (
            error.message === 'Network Error' ||
            error.response.data.message === 'Token has expired'
         ) {
            navigate('/');

            logOutIfTokenExpired(t('tokenExpired.tokenExpired'), t('tokenExpired.close'));
         }

         console.error('Could not get user data in profile page of favourite list', error);
      }
   };

   const handleChange = (e) => {
      const { name, value } = e.target;

      setMailReply((prev) => ({
         ...prev,
         [name]: value,
      }));

      setErrors((prev) => ({
         ...prev,
         [name]: '',
      }));
   };

   const handleReply = async (slug) => {
      try {
         const newErrors = {};

         if (mailReply.subject === '') {
            newErrors.subject = t('employerFavouriteListProfile.subjectRequired');
            window.scrollTo(0, 0);
         }
         if (mailReply.message === '') {
            newErrors.message = t('employerFavouriteListProfile.messageRequired');
            window.scrollTo(0, 0);
         }

         setErrors(newErrors);
         setMailSent(true);

         if (Object.keys(newErrors).length === 0) {
            const confirmationResult = await Swal.fire({
               icon: 'question',
               showCancelButton: true,
               cancelButtonText: t('employerFavouriteListProfile.no'),
               confirmButtonText: t('employerFavouriteListProfile.yes'),
               text: t('employerFavouriteListProfile.sendMailConfirmTxt'),
               title: t('employerFavouriteListProfile.sendMailConfirmTitle'),
            });

            if (confirmationResult.isConfirmed) {
               const updatedReply = {
                  ...mailReply,
                  id: favouriteListProfileData.cv.user_id,
                  selectedFileName,
               };

               const formData = new FormData();
               selectedFileName.forEach((fileName, index) => {
                  formData.append(`selectedFileNames[${index}]`, fileName);
               });

               setLoading(true);

               const response = await axios.post(
                  BaseApi + `/candidates/sendmailjobseeker/${slug}`,
                  updatedReply,
                  {
                     headers: {
                        key: ApiKey,
                        token: tokenKey,
                        contentTypy: 'application/json',
                     },
                  }
               );

               setLoading(false);
               setMailSent(false);

               if (response.data.status === 200) {
                  setMailReply({
                     ...mailReply,
                     subject: '',
                     message: '',
                  });

                  setSelectedFileName([]);

                  const fileInput = document.getElementById('formFile');

                  if (fileInput) {
                     fileInput.value = '';
                  }

                  // closeModal();

                  resultInfo(
                     'success',
                     t('employerFavouriteListProfile.sendMailSuccessTitle'),
                     t('employerFavouriteListProfile.close')
                  );
               } else if (response.data.status === 400) {
                  navigate('/');

                  Cookies.remove('fname');
                  Cookies.remove('user_id');
                  Cookies.remove('user_type');
                  Cookies.remove('tokenClient');

                  // closeModal();

                  resultInfo(
                     'warning',
                     response.data.message,
                     t('employerFavouriteListProfile.close')
                  );
               } else {
                  // closeModal();

                  Swal.fire({
                     icon: 'error',
                     text: response.data.message,
                     confirmButtonText: t('employerFavouriteListProfile.close'),
                     title: t('employerFavouriteListProfile.sendMailFailedTitle'),
                  });
               }
            }
         }
      } catch (error) {
         setLoading(false);
         // closeModal();

         if (
            error.message === 'Network Error' ||
            error.response.data.message === 'Token has expired'
         ) {
            navigate('/');

            logOutIfTokenExpired(t('tokenExpired.tokenExpired'), t('tokenExpired.close'));
         } else {
            Swal.fire({
               icon: 'error',
               text: t('employerFavouriteListProfile.sendMailFailedTxt'),
               confirmButtonText: t('employerFavouriteListProfile.close'),
               title: t('employerFavouriteListProfile.sendMailFailedTitle'),
            });
         }
      }
   };

   useEffect(() => {
      if (!tokenKey) {
         navigate('/user/employerlogin');
      } else {
         getData();

         window.scrollTo(0, 0);
      }
   }, [tokenKey, navigate, userType]); // eslint-disable-line

   if (loading && !userData) {
      return (
         <Container>
            <Flex justify="center" align="center" height="516px">
               <Spinner size={3}></Spinner>
            </Flex>
         </Container>
      );
   }

   return (
      <Container>
         {userData ? (
            <>
               <div className="container my-5 JSMyCV">
                  <div className="row">
                     <Flex gap="4">
                        <AddToFavorites
                           simpleCv={userData.simpleCv}
                           disabled={userData.fav_status}
                           refetch={getData}
                        />
                        <DownloadSimpleCvPdf
                           simpleCv={userData.simpleCv}
                           isOpenContact={userData.is_open_contact}         
                        />
                     </Flex>
                  </div>
                  <Questionnaire
                     cvDetails={userData}
                     isOpenContact={userData.is_open_contact}
                     refetch={getData}
                  />
               </div>
               <div
                  tabIndex="-1"
                  id="ReplyModal"
                  aria-hidden="true"
                  className="modal fade"
                  aria-labelledby="exampleModalLabel"
               >
                  <div className="modal-dialog">
                     <div className="modal-content">
                        <div className="modal-header">
                           <h1 className="modal-title fs-5" id="exampleModalLabel">
                              {t('employerFavouriteListProfile.sendMailTo')} {userData.first_name}{' '}
                              {userData.last_name}
                           </h1>
                           <button
                              type="button"
                              aria-label="Close"
                              className="btn-close"
                              data-bs-dismiss="modal"
                           ></button>
                        </div>
                        <div className="modal-body FLPReplyModal">
                           <div className="form-outline mb-4 mt-4 DashBoardInputBx">
                              <label className="form-label" htmlFor="form3Example3">
                                 {t('employerFavouriteListProfile.subject')}{' '}
                                 <span className="RedStar">*</span>
                              </label>
                              <input
                                 type="text"
                                 id="subject"
                                 name="subject"
                                 onChange={handleChange}
                                 value={mailReply.subject}
                                 placeholder={t('employerFavouriteListProfile.subject')}
                                 className={`form-control ${errors.subject && 'input-error'}`}
                              />
                              {errors.subject && (
                                 <div className="text-danger">{errors.subject}</div>
                              )}
                           </div>
                           <div className="form-outline mb-4 DashBoardInputBx">
                              <label className="form-label" htmlFor="form3Example3">
                                 {t('employerFavouriteListProfile.message')}{' '}
                                 <span className="RedStar">*</span>
                              </label>
                              <textarea
                                 rows={6}
                                 id="message"
                                 name="message"
                                 onChange={handleChange}
                                 value={mailReply.message}
                                 placeholder={t('employerFavouriteListProfile.message')}
                                 className={`form-control FLPTextarea ${errors.message && 'input-error'}`}
                              ></textarea>
                              {errors.message && (
                                 <div className="text-danger">{errors.message}</div>
                              )}
                           </div>
                           <button
                              type="button"
                              aria-label={mailSent && 'Close'}
                              onClick={() => handleReply('slug')} //TODO: slug
                              className="btn btn-primary button1"
                              data-bs-dismiss={mailSent && 'modal'}
                           >
                              {t('employerFavouriteListProfile.submitButton')}
                           </button>
                        </div>
                     </div>
                  </div>
               </div>{' '}
            </>
         ) : (
            <>Error</>
         )}
      </Container>
   );
};

export const SimpleCV = observer(QuestionnaireView);
